import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ShopService } from '@app/services/shops/shop.service';
import { ProductSearchService } from '@pages/engage/smart-cart/services/product-search.service';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { ProductListItem } from '@storybook/components/product-list/product-list.component';
import { CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import { GiftMode, GoalFormShape, GoalOfferProduct } from '../../../goals-progress-bar.model';
import {
  DISCOUNT_LABEL_PLACEHOLDER,
  DISCOUNT_TYPE_OPTIONS,
} from '@pages/engage/smart-cart/smart-cart-form/discount.model';

@Component({
  selector: 'smart-cart-goal-offer-tab',
  templateUrl: './goal-offer-tab.component.html',
  styleUrls: ['../../../../../components/shared.scss'],
  standalone: false,
})
export class SmartCartGoalOfferTabComponent implements OnInit {
  @Input({ required: true }) goal!: FormGroup<GoalFormShape>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly productSearchService = inject(ProductSearchService);
  private readonly shopService = inject(ShopService);

  readonly offerTypeOptions: DropdownOption[] = [
    { label: 'Free Shipping', value: 'freeShipping' },
    { label: 'Order discount', value: 'orderDiscount' },
    { label: 'Product', value: 'product' },
  ];

  readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;
  readonly discountLabelPlaceholder = DISCOUNT_LABEL_PLACEHOLDER;

  currencySymbol: CurrencySign = '$';

  ngOnInit(): void {
    this.currencySymbol = this.shopService.shopCurrencySymbol ?? '$';
    this.registerGiftModeSwitch();
  }

  onAddProducts(): void {
    const productGroup = this.goal.controls.offer.controls.product;
    const isMultiple = productGroup.controls.giftMode.value === 'multiple';
    const currentProducts = productGroup.controls.products.value;

    const preSelectedProducts: SelectableProduct[] = currentProducts.map((product) => ({
      id: product.id,
      handle: product.handle,
      title: product.name,
      sku: product.sku,
      price: product.price,
      imageUrl: product.imageUrl,
      selectedOptions: product.selectedOptions,
    }));

    const config: AddProductsModalConfig = {
      title: 'Add Products',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      excludeProductIds: currentProducts.map((product) => product.id),
      enableVariants: true,
      enableEdit: !isMultiple,
      preSelectedProducts: isMultiple ? [] : preSelectedProducts,
      canChooseOnlyOneProduct: !isMultiple,
      hideSelectAll: true,
      fetchProducts: (query) => this.fetchProducts(query),
    };

    this.dialog
      .open(AddProductsModalComponent, {
        data: config,
        panelClass: 'add-products-modal-container',
        width: '700px',
        maxHeight: '80vh',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: SelectableProduct[] | undefined) => {
        if (!result?.length) return;

        const newProducts: GoalOfferProduct[] = result.map((product) => ({
          id: product.id,
          handle: product.handle,
          name: product.title,
          description: product.description,
          imageUrl: product.imageUrl,
          sku: product.sku,
          price: product.price,
          ...(product.selectedOptions ? { selectedOptions: product.selectedOptions } : {}),
        }));

        const existingIds = new Set(currentProducts.map((product) => product.id));
        const additions = newProducts.filter((product) => !existingIds.has(product.id));
        const updated = isMultiple
          ? [...currentProducts, ...additions].map((product, index) => ({ ...product, priority: index }))
          : [{ ...newProducts[0], priority: 0 }];

        productGroup.controls.products.setValue(updated);

        if (isMultiple || !this.isSingleSpecificVariantSelected(updated)) {
          productGroup.controls.hideIfProductIsInCart.setValue(false);
        }
      });
  }

  onRemoveProduct(productId: string | number): void {
    const control = this.goal.controls.offer.controls.product.controls.products;
    control.setValue(control.value.filter((product) => product.id !== productId));
  }

  onReorderProducts(reordered: ProductListItem[]): void {
    const control = this.goal.controls.offer.controls.product.controls.products;
    const current = control.value;
    control.setValue(
      reordered.map((item, index) => ({
        ...(current.find((product) => product.id === item.id) as GoalOfferProduct),
        priority: index,
      })),
    );
  }

  onExternalLink(product: ProductListItem): void {
    const productId =
      typeof product.id === 'string' && product.id.includes('/') ? product.id.split('/').pop() : product.id;
    const shop = this.shopService.shopSignal();
    const trimmedShopName = shop.name.split('.myshopify.com')[0];
    window.open(`https://admin.shopify.com/store/${trimmedShopName}/products/${productId}`, '_blank');
  }

  private async fetchProducts(searchQuery: string): Promise<SelectableProduct[]> {
    const products = await firstValueFrom(this.productSearchService.searchProducts(searchQuery));
    return products.map((product) => (product.hasUniformPrice === false ? { ...product, price: undefined } : product));
  }

  private isSingleSpecificVariantSelected(products: GoalOfferProduct[]): boolean {
    if (!products.length) return false;
    const product = products[0];
    if (!product.selectedOptions?.length) return true;
    return product.selectedOptions.every((option) => option.values?.length === 1);
  }

  // Mirrors the gift-unlock behavior: switching Multiple → Single stashes and clears the product list;
  // switching Single → Multiple restores the stash when the list is empty.
  private registerGiftModeSwitch(): void {
    const productGroup = this.goal.controls.offer.controls.product;
    const productsControl = productGroup.controls.products;

    let previousGiftMode: GiftMode = productGroup.controls.giftMode.value;
    let savedMultipleProducts: GoalOfferProduct[] = [];

    productGroup.controls.giftMode.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => {
        const currentProducts = productsControl.value;
        if (mode === 'single') {
          if (previousGiftMode === 'multiple') {
            savedMultipleProducts = [...currentProducts];
          }
          productsControl.setValue([]);
        } else if (mode === 'multiple') {
          if (!currentProducts.length && savedMultipleProducts.length) {
            productsControl.setValue(savedMultipleProducts);
          }
          savedMultipleProducts = [];
        }
        previousGiftMode = mode;
      });
  }
}
